import { Trans, useTranslation } from "react-i18next";
import type { AnalysisSummary } from "@repo/shared";

import { formatBytes } from "@/lib/health-format";

interface SummaryLineProps {
  summary: AnalysisSummary;
}

// Renders the inline-summary value (e.g. "224") in emphasised mono ink, while the
// surrounding labels stay faint — so values stand out from their labels.
const SUMMARY_VALUE = { v: <span className="font-mono font-medium text-ink" /> };

function formatPct(part: number, total: number): string {
  return total > 0 ? ((part / total) * 100).toFixed(1) : "0";
}

export function FieldsInlineSummary({ summary }: SummaryLineProps) {
  const { t } = useTranslation("analyses");
  const unusedMemory = summary.wastedBytes + summary.wastedIndexBytes;
  return (
    <p className="text-sm text-dim">
      <Trans
        t={t}
        i18nKey="inlineSummary.fields"
        values={{
          total: summary.totalFields,
          used: summary.usedFields,
          usedPct: formatPct(summary.usedFields, summary.totalFields),
          unused: summary.unusedFields,
          unusedPct: formatPct(summary.unusedFields, summary.totalFields),
          memory: formatBytes(unusedMemory),
        }}
        components={SUMMARY_VALUE}
      />
    </p>
  );
}

export function VariablesInlineSummary({ summary }: SummaryLineProps) {
  const { t } = useTranslation("analyses");
  return (
    <p className="text-sm text-dim">
      <Trans
        t={t}
        i18nKey="inlineSummary.variables"
        values={{
          total: summary.totalVariables,
          used: summary.usedVariables,
          usedPct: formatPct(summary.usedVariables, summary.totalVariables),
          unused: summary.unusedVariables,
          unusedPct: formatPct(summary.unusedVariables, summary.totalVariables),
        }}
        components={SUMMARY_VALUE}
      />
    </p>
  );
}

export function DimensionsInlineSummary({ summary }: SummaryLineProps) {
  const { t } = useTranslation("analyses");
  const unusedDimensions = summary.totalDimensions - summary.usedDimensions;
  return (
    <p className="text-sm text-dim">
      <Trans
        t={t}
        i18nKey="inlineSummary.dimensions"
        values={{
          total: summary.totalDimensions,
          used: summary.usedDimensions,
          usedPct: formatPct(summary.usedDimensions, summary.totalDimensions),
          unused: unusedDimensions,
          unusedPct: formatPct(unusedDimensions, summary.totalDimensions),
          broken: summary.brokenDimensions,
          brokenPct: formatPct(summary.brokenDimensions, summary.totalDimensions),
        }}
        components={SUMMARY_VALUE}
      />
    </p>
  );
}

export function MeasuresInlineSummary({ summary }: SummaryLineProps) {
  const { t } = useTranslation("analyses");
  const unusedMeasures = summary.totalMeasures - summary.usedMeasures;
  return (
    <p className="text-sm text-dim">
      <Trans
        t={t}
        i18nKey="inlineSummary.measures"
        values={{
          total: summary.totalMeasures,
          used: summary.usedMeasures,
          usedPct: formatPct(summary.usedMeasures, summary.totalMeasures),
          unused: unusedMeasures,
          unusedPct: formatPct(unusedMeasures, summary.totalMeasures),
          broken: summary.brokenMeasures,
          brokenPct: formatPct(summary.brokenMeasures, summary.totalMeasures),
        }}
        components={SUMMARY_VALUE}
      />
    </p>
  );
}
